import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'

import { cn } from '@/lib/utils'
import { CloseIcon } from '@/components/Icons'

/**
 * v2 · 06 Popup anatomy.
 *
 * Sold-out and checkout both use this shell: surface card at radius
 * 24 with a hairline, one deep soft shadow, centred over the 0.7
 * scrim — on every viewport; the bottom sheet is gone. Title block
 * (22/700 + 15 muted description), full-bleed hairline, content,
 * white CTA, quiet footer.
 */

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogClose = DialogPrimitive.Close
const DialogPortal = DialogPrimitive.Portal

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'sss-scrim fixed inset-0 z-40',
      'data-[state=open]:animate-[sssFadeIn_320ms_var(--sss-ease)]',
      'data-[state=closed]:animate-[sssFadeOut_320ms_var(--sss-ease-exit)]',
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-60 w-100 max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2',
        'overflow-hidden rounded-xl border border-hairline bg-surface text-foreground',
        'shadow-overlay focus:outline-none',
        // A tall popup on a short phone must not run off the screen and
        // strand its CTA. Column layout so a body marked scrollable
        // takes the overflow while the media above it stays put; dvh
        // rather than vh so the mobile browser chrome is accounted for.
        'flex max-h-[92dvh] flex-col',
        'data-[state=open]:animate-[sssZoomIn_320ms_var(--sss-ease)]',
        'data-[state=closed]:animate-[sssZoomOut_320ms_var(--sss-ease-exit)]',
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * Title block — title (and description) left, 32px close target
 * pinned top-right. A full-bleed hairline separates it from the
 * content below.
 */
function DialogHeader({
  className,
  children,
  onClose,
  closeLabel = 'Close',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { onClose?: () => void; closeLabel?: string }) {
  return (
    <div
      className={cn('relative border-b border-hairline px-6 pb-5 pt-6', className)}
      {...props}
    >
      <div className="pr-9">{children}</div>
      <DialogClose
        aria-label={closeLabel}
        onClick={onClose}
        className="absolute right-5 top-5 flex size-8 cursor-pointer items-center justify-center text-ink-muted transition-colors duration-[120ms] hover:text-ink focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_4px_var(--sss-ring)]"
      >
        <CloseIcon size={18} />
      </DialogClose>
    </div>
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('text-[22px] font-bold tracking-[-0.02em]', className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('mt-2 text-[15px] leading-[1.5] text-ink-muted', className)}
      {...props}
    />
  )
}

/**
 * Body — the 24-padded region below the hairline, and the part that
 * gives when the popup is taller than the viewport. `min-h-0` is what
 * actually lets it shrink: a flex child defaults to min-height:auto
 * and would otherwise refuse to scroll and push the CTA off-screen.
 */
function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex min-h-0 flex-col gap-4 overflow-y-auto p-6', className)} {...props} />
  )
}

/** Actions — stacked, primary first; the white CTA never shares a row. */
function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-2.5', className)} {...props} />
}

export {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
}
